"use client";

import type { ReactNode } from "react";
import type { ChoghadiyaResponse, ChoghadiyaTimePeriod } from "@/lib/api/panchang";
import { ChoghadiyaSummary } from "./ChoghadiyaSummary";
import { ChoghadiyaLegend } from "./ChoghadiyaLegend";
import { ChoghadiyaTable } from "./ChoghadiyaTable";
import { CHOGHADIYA_RESULT_COPY, type ChoghadiyaLanguage } from "./copy";

/** Windows after the first one (the first is already shown); two only on Saturday. */
function laterWindows(windows: ChoghadiyaTimePeriod[] | undefined): ChoghadiyaTimePeriod[] {
  return (windows ?? []).slice(1);
}

interface ChoghadiyaResultProps {
  result: ChoghadiyaResponse;
  resultOffer: ReactNode;
  language?: ChoghadiyaLanguage;
}

export function ChoghadiyaResult({ result, resultOffer, language = "en" }: ChoghadiyaResultProps) {
  const copy = CHOGHADIYA_RESULT_COPY[language];
  return (
    <div className="space-y-6">
      <ChoghadiyaSummary result={result} language={language} />

      {resultOffer}

      <ChoghadiyaLegend language={language} />

      <ChoghadiyaTable
        title={copy.dayTitle}
        titleHi={copy.dayTitleAlt}
        language={language}
        periods={result.day_choghadiya}
        timeRange={`${result.sunrise} - ${result.sunset}`}
      />

      <ChoghadiyaTable
        title={copy.nightTitle}
        titleHi={copy.nightTitleAlt}
        language={language}
        periods={result.night_choghadiya}
        timeRange={`${result.sunset} - ${result.next_sunrise}`}
      />

      {/* Vaar Vela / Kaal Vela / Kaal Ratri */}
      {result.vaar_vela && (
        <div className="card-sacred p-4 space-y-3">
          <h3
            className="text-body font-bold"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            {copy.inauspicious}
            <span
              className="block text-caption font-normal mt-0.5"
              style={{ color: "var(--ish-text-muted)" }}
            >
              {copy.inauspiciousAlt}
            </span>
          </h3>
          <p className="text-caption" style={{ color: "var(--ish-text-muted)" }}>
            {copy.inauspiciousBody}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              {
                label: copy.vaarVela,
                labelHi: language === "hi" ? "Vaar Vela" : "वार वेला",
                ...result.vaar_vela,
                also: [],
              },
              {
                label: copy.kaalVela,
                labelHi: language === "hi" ? "Kaal Vela" : "काल वेला",
                ...result.kaal_vela,
                also: laterWindows(result.kaal_vela_windows),
              },
              {
                label: copy.kaalRatri,
                labelHi: language === "hi" ? "Kaal Ratri" : "काल रात्रि",
                ...result.kaal_ratri,
                also: laterWindows(result.kaal_ratri_windows),
              },
            ].map(({ label, labelHi, start_time, end_time, also }) => (
              <div
                key={label}
                className="rounded-lg p-3 text-center"
                style={{
                  background: "var(--color-error-bg)",
                  border: "1px solid var(--color-error-tint)",
                }}
              >
                <p
                  className="text-[11px] font-semibold uppercase tracking-wide mb-0.5"
                  style={{ color: "var(--color-error)" }}
                >
                  {label}
                </p>
                <p
                  className="text-[10px] mb-1"
                  style={{ color: "var(--color-error)", opacity: 0.7 }}
                >
                  {labelHi}
                </p>
                <p className="text-body-small font-bold" style={{ color: "var(--ish-dark)" }}>
                  {start_time} – {end_time}
                  {also.map((w) => (
                    <span key={w.start_time} className="block">
                      {copy.and} {w.start_time} – {w.end_time}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
